import { defineStore } from 'pinia'
import { hcaClientFactory } from '@/api/factories/HcaClientFactory'

const MAX_CACHE_SIZE = 50

async function fetchBlob(imagePath: string): Promise<string> {
    const response = await hcaClientFactory.imagesApi.downloadImage({ filePath: imagePath }, {
        responseType: 'blob',
    })
    const blob = response.data as unknown as Blob
    return window.URL.createObjectURL(blob)
}

export const useImageBlobCacheStore = defineStore('imageBlobCache', {
    state: () => ({
        cache: new Map<string, string>(),
        pending: new Map<string, Promise<string>>(),
    }),
    actions: {
        evictOldest() {
            while (this.cache.size > MAX_CACHE_SIZE) {
                const oldestKey = this.cache.keys().next().value!
                const url = this.cache.get(oldestKey)!
                window.URL.revokeObjectURL(url)
                this.cache.delete(oldestKey)
            }
        },

        getCachedImageUrl(imagePath: string): string | null {
            return this.cache.get(imagePath) ?? null
        },

        async getImageUrl(imagePath: string): Promise<string> {
            const cached = this.cache.get(imagePath)
            if (cached) {
                this.cache.delete(imagePath)
                this.cache.set(imagePath, cached)
                return cached
            }

            const inflight = this.pending.get(imagePath)
            if (inflight) {
                return inflight
            }

            const promise = fetchBlob(imagePath)
                .then((objectUrl) => {
                    this.cache.set(imagePath, objectUrl)
                    this.evictOldest()
                    return objectUrl
                })
                .finally(() => {
                    this.pending.delete(imagePath)
                })

            this.pending.set(imagePath, promise)
            return promise
        },

        async getImageBlob(imagePath: string): Promise<Blob> {
            const url = await this.getImageUrl(imagePath)
            const response = await fetch(url)
            return response.blob()
        },
    },
})
